import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiClientError,
  type AuthStep,
  type ChannelProvider,
  type Instance,
  type Page,
  type QrCode,
} from '../api/types.js'
import { IconInstances, IconRefresh } from '../icons.js'
import { Alert } from '../ui/Alert.js'
import { якорьНомера } from '../router.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { вКавычках } from '../ui/quotes.js'
import { useToaster } from '../ui/toasts.js'
import { AuthPanel } from './AuthPanel.js'

/**
 * Узкий срез клиента: экрану нужны только операции над инстансами
 * (барьер 2 — клиент API и есть граница процесса для экранов админки,
 * его двойник в тестах разрешён, см. Login.tsx).
 */
export interface InstancesClient {
  instances(): Promise<Page<Instance>>
  createInstance(name: string, provider: ChannelProvider): Promise<Instance>
  submitAuth(id: string, step: AuthStep): Promise<void>
  startInstance(id: string): Promise<Instance>
  stopInstance(id: string): Promise<Instance>
  removeInstance(id: string): Promise<void>
  qr(id: string): Promise<QrCode | null>
  resetInstance(id: string): Promise<void>
}

export interface InstancesProps {
  client: InstancesClient
  /** Права, выданные при входе (session.permissions()) — экран сам их не запрашивает. */
  permissions: string[]
}

const CAN_WRITE = 'instances.write'

// Статусы, в которых код ещё может появиться или уже показан и ждёт
// сканирования — только для них имеет смысл держать QR-панель открытой
// и обновлять список чаще (FR-ADM-005). Строки, а не импорт из
// module-whatsapp: экран не должен тянуть серверный пакет ради констант.
// authenticating добавлен вместе с объяснением состояний (FR-ADM-015) —
// телефон уже отсканировал код и ждёт подтверждения WhatsApp, опрос обязан
// продолжаться, а не останавливаться раньше времени.
const PENDING_STATUSES = new Set([
  'starting',
  'qr_pending',
  // Ожидание кода и пароля — тоже «панель входа открыта»: человек вводит
  // их там же, где сканирует код, и опрос обязан продолжаться.
  'code_pending',
  'password_pending',
  'authenticating',
  'reconnecting',
])

const RESTARTABLE_STATUSES = new Set(['created', 'disconnected', 'logged_out', 'failed'])

/**
 * Состояния, из которых номер можно остановить (FR-ADM-035).
 *
 * Не только `connected`. Номер, запущенный по ошибке, застревает на
 * «Ожидает сканирования»: код висит, опрос идёт, а единственной кнопкой на
 * карточке было «Удалить» — вместе с перепиской, контактами и подписками.
 * Остановка в этих состояниях — то же самое, что и на связи: канал гасится,
 * привязка (если она была) остаётся, номер переходит в «Отключён» и ждёт.
 *
 * `logged_out` и `failed` сюда не входят: канал там уже не работает,
 * останавливать нечего — им нужен запуск или сброс авторизации.
 */
const STOPPABLE_STATUSES = new Set([
  'connected',
  'starting',
  'qr_pending',
  'code_pending',
  'password_pending',
  'authenticating',
  'reconnecting',
])

const STATUS_LABELS: Record<string, string> = {
  created: 'Создан',
  starting: 'Запускается',
  qr_pending: 'Ожидает сканирования',
  code_pending: 'Ожидает кода',
  password_pending: 'Ожидает пароля',
  authenticating: 'Авторизуется',
  connected: 'Подключён',
  disconnected: 'Отключён',
  logged_out: 'Вышел из сети',
  failed: 'Ошибка',
  reconnecting: 'Переподключается',
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status
}

/**
 * Смысл цвета ярлыка (FR-ADM-015, раздел 6 спецификации порталов, правило 1):
 * зелёный — работает, синий — идёт процесс сам по себе, жёлтый — остановлен
 * и ждёт человека, красный — сломан и нужно вмешательство.
 */
type StatusTone = 'ok' | 'progress' | 'warn' | 'danger' | 'neutral'

/**
 * Как канал называется человеку. Названия провайдеров пишутся так, как их
 * пишут сами провайдеры, — иначе получилось бы «Вотсап» рядом с «Telegram».
 */
const ИМЯ_КАНАЛА: Record<ChannelProvider, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
}

const STATUS_TONE: Record<string, StatusTone> = {
  created: 'neutral',
  starting: 'progress',
  qr_pending: 'progress',
  code_pending: 'progress',
  password_pending: 'progress',
  authenticating: 'progress',
  connected: 'ok',
  disconnected: 'warn',
  logged_out: 'danger',
  failed: 'danger',
  reconnecting: 'progress',
}

/**
 * Объяснение состояния для человека, а не для разработчика: что это значит,
 * что сделает система сама, что может сделать человек (FR-ADM-015). Тексты
 * общие для состояния — причина конкретного отказа приходит отдельно, в
 * instance.lastError, и показывается рядом (FR-ADM-006).
 */
interface StatusMeta {
  explanation: string
  system: string
  human: string
}

/*
 * Три строки на состояние. Первая говорит, что сейчас с номером; вторая —
 * что делает система, чтобы человек не пытался помочь там, где помощь не
 * нужна; третья — что сделать ему, и только если это правда нужно.
 *
 * Сроки названы там, где они известны: «несколько секунд» отличает
 * нормальное ожидание от зависшего, а без них человек нажимает «Запустить»
 * второй раз посреди подключения.
 */
const STATUS_META: Record<string, StatusMeta> = {
  created: {
    explanation: 'Номер ещё не подключён: он заведён, но ни разу не запускался.',
    system: 'Ничего не происходит: подключение начинается по вашей команде.',
    human: 'Нажмите «Запустить» — портал покажет, что нужно для входа.',
  },
  starting: {
    explanation: 'Готовим подключение.',
    system: 'Канал поднимается сам, обычно за несколько секунд, и спросит то, что нужно для входа.',
    human: 'Подождите. Что делать дальше, появится здесь же.',
  },
  qr_pending: {
    explanation: 'Код готов — осталось отсканировать его телефоном.',
    system: 'Код обновляется сам, пока вы его не отсканируете. Просроченный заменяется новым.',
    human: 'Откройте на телефоне список подключённых устройств и наведите камеру на код ниже.',
  },
  code_pending: {
    explanation: 'Провайдер прислал код подтверждения на номер.',
    system: 'Ждём код. Пока он не введён, подключение не продолжится.',
    human: 'Введите код из сообщения в поле ниже.',
  },
  password_pending: {
    explanation: 'У аккаунта включена двухфакторная защита.',
    system: 'Код принят. Остался пароль — без него вход не завершится.',
    human: 'Введите пароль двухфакторной защиты в поле ниже. Это не код из сообщения.',
  },
  authenticating: {
    explanation: 'Данные приняты, провайдер подтверждает вход.',
    system: 'Ждём ответа серверов провайдера — обычно несколько секунд.',
    human: 'Подождите, страницу обновлять не нужно.',
  },
  connected: {
    explanation: 'Номер на связи: сообщения приходят и отправляются.',
    system: 'Соединение под присмотром — если оно оборвётся, восстановим сами, не теряя привязку.',
    human: 'Ничего. Можно работать.',
  },
  reconnecting: {
    explanation: 'Связь оборвалась, восстанавливаем подключение.',
    system: 'Пробуем снова с нарастающими паузами. Привязка телефона сохраняется, сканировать заново не придётся.',
    human: 'Подождите. Если состояние не меняется дольше нескольких минут, остановите номер и запустите снова.',
  },
  disconnected: {
    explanation: 'Номер остановлен: сообщения не приходят и не отправляются.',
    system: 'Сам не поднимется — так задумано, остановка остаётся в силе до вашей команды.',
    human: 'Нажмите «Запустить», когда номер снова понадобится. Сканировать код заново не нужно.',
  },
  logged_out: {
    explanation: 'Сеанс завершён на стороне провайдера — обычно это делают с телефона, в списке подключённых устройств.',
    system: 'Переподключаться не будем: прежней сессии больше нет, и восстановить её нечем.',
    human: 'Нажмите «Сбросить авторизацию», затем «Запустить» — и войдите заново.',
  },
  failed: {
    explanation: 'Подключение сорвалось, и попытки исчерпаны.',
    system: 'Остановились, чтобы не повторять одну и ту же ошибку без конца.',
    human: 'Прочитайте причину ниже. Если дело в привязке — «Сбросить авторизацию» и заново; в остальных случаях достаточно «Запустить».',
  },
}

/**
 * Уточнения там, где путь входа у каналов разный.
 *
 * Общая таблица выше говорит канало-нейтрально — иначе владельцу
 * телеграм-номера предлагалось бы открыть WhatsApp. Но нейтральность не
 * должна доходить до бесполезности: «откройте список устройств» без
 * указания, где он лежит, — не подсказка, а отписка. Поэтому там, где
 * шаги действительно различаются, они названы поимённо.
 */
const STATUS_META_BY_PROVIDER: Record<ChannelProvider, Partial<Record<string, StatusMeta>>> = {
  whatsapp: {
    starting: {
      explanation: 'Готовим подключение к WhatsApp.',
      system: 'Канал поднимается сам, обычно за несколько секунд, и покажет код привязки.',
      human: 'Подождите. Код появится здесь же.',
    },
    qr_pending: {
      explanation: 'Код готов — осталось отсканировать его телефоном.',
      system: 'Код обновляется сам, пока вы его не отсканируете. Просроченный заменяется новым.',
      human: 'На телефоне: WhatsApp → Настройки → Связанные устройства → Привязка устройства. Наведите камеру на код ниже.',
    },
    authenticating: {
      explanation: 'Код принят, WhatsApp подтверждает привязку.',
      system: 'Ждём ответа серверов WhatsApp — обычно несколько секунд.',
      human: 'Подождите, страницу обновлять не нужно.',
    },
    logged_out: {
      explanation: 'Устройство отвязано на стороне WhatsApp — обычно это делают с телефона в списке связанных устройств.',
      system: 'Переподключаться не будем: прежней сессии больше нет, и восстановить её нечем.',
      human: 'Нажмите «Сбросить авторизацию», затем «Запустить» — и отсканируйте новый код.',
    },
  },
  telegram: {
    starting: {
      explanation: 'Готовим подключение к Telegram.',
      system: 'Канал поднимается сам, обычно за несколько секунд, и спросит код или покажет код для входа с устройства.',
      human: 'Подождите. Что делать дальше, появится здесь же.',
    },
    qr_pending: {
      explanation: 'Код для входа с другого устройства готов.',
      system: 'Код обновляется сам, пока вы его не отсканируете.',
      human: 'На телефоне: Telegram → Настройки → Устройства → Подключить устройство. Наведите камеру на код ниже.',
    },
    code_pending: {
      explanation: 'Telegram прислал код подтверждения на номер.',
      system: 'Ждём код. Он приходит сообщением в сам Telegram, а если войти некуда — по SMS.',
      human: 'Введите код в поле ниже. Если код не подошёл, Telegram пришлёт новый.',
    },
    password_pending: {
      explanation: 'У аккаунта включена двухфакторная защита Telegram.',
      system: 'Код принят. Остался облачный пароль — без него вход не завершится.',
      human: 'Введите облачный пароль в поле ниже. Это не код из сообщения и не пароль от почты.',
    },
    logged_out: {
      explanation: 'Сеанс завершён на стороне Telegram — обычно это делают с телефона, в списке активных устройств.',
      system: 'Переподключаться не будем: прежней сессии больше нет, и восстановить её нечем.',
      human: 'Нажмите «Сбросить авторизацию», затем «Запустить» — и войдите заново.',
    },
  },
}

function statusMeta(status: string, provider: ChannelProvider): StatusMeta {
  return (
    STATUS_META_BY_PROVIDER[provider]?.[status] ??
    STATUS_META[status] ?? {
      explanation: `Состояние ${вКавычках(status)} этой версии портала незнакомо.`,
      system: 'Само по себе ничего не произойдёт: что делать в этом состоянии, портал не знает.',
      human: 'Обратитесь к разработчику платформы, если состояние не меняется.',
    }
  )
}

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

// Тот же интервал, что у AuthPanel: список не должен отставать от кода,
// иначе строка инстанса ещё несколько секунд показывает «Ожидает
// сканирования» после того, как он уже подключился.
const POLL_INTERVAL_MS = 3000

export function Instances(props: InstancesProps) {
  const { client, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()
  const удаление = useConfirmation<Instance>()
  const сброс = useConfirmation<Instance>()

  const [instances, setInstances] = useState<Instance[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [provider, setProvider] = useState<ChannelProvider>('whatsapp')
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)
  // Инстансы с действием в процессе — их кнопки блокируются, чтобы повторное
  // нажатие не отправило то же действие дважды (тот же приём, что в Login).
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())
  // Идёт фоновый опрос (не первичная загрузка) — список из предыдущего
  // ответа остаётся на экране без изменений до прихода нового (FR-ADM-017,
  // правило 8 раздела 6 спецификации: оформление не может мигать данными
  // при каждом тике опроса). Индикатор ненавязчивый — рядом с заголовком,
  // не поверх содержимого.
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (options.silent === true) setRefreshing(true)
      try {
        const page = await client.instances()
        // Список заменяется одним присваиванием только здесь, после того как
        // новые данные полностью пришли — старые данные ни на миг не
        // исчезают с экрана раньше времени (setInstances(null) нигде на
        // пути опроса не вызывается).
        setInstances(page.items)
      } catch (cause) {
        setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
      } finally {
        if (options.silent === true) setRefreshing(false)
      }
    },
    [client],
  )

  useEffect(() => {
    void load()
  }, [load])

  // Последний известный список читается тиком опроса через ref, а не через
  // зависимость эффекта: интервал заводится один раз при монтировании, а не
  // пересоздаётся при каждом обновлении списка — так первый тик не зависит
  // от того, успел ли уже отработать первичный запрос (иначе интервал
  // появлялся бы только после разрешения промиса, и таймер оказывался бы
  // «моложе» на неопределённое время, что на подложных таймерах теста
  // ведёт себя нестабильно).
  const instancesRef = useRef<Instance[] | null>(null)
  useEffect(() => {
    instancesRef.current = instances
  }, [instances])

  useEffect(() => {
    // Опрос списка продолжается, пока хоть один инстанс не подключился,
    // и прекращается сам собой, как только ожидающих не остаётся — тот же
    // принцип, что у AuthPanel, но на уровне всего экрана (FR-ADM-005).
    const timer = setInterval(() => {
      const hasPending = (instancesRef.current ?? []).some((instance) =>
        PENDING_STATUSES.has(instance.status),
      )
      if (!hasPending) {
        clearInterval(timer)
        return
      }
      void load({ silent: true })
    }, POLL_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [load])

  /**
   * Действие над номером. `success` — что сказать, когда получилось; у
   * запуска и остановки его нет: там ответ виден на самой карточке —
   * состояние меняется, — и уведомление повторяло бы её словами.
   */
  async function runAction(id: string, action: () => Promise<unknown>, success?: string) {
    if (busy.has(id)) return
    setBusy((current) => new Set(current).add(id))
    setError(null)
    try {
      await action()
      await load()
      if (success !== undefined) toaster.succeeded(success)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (creating || name.trim() === '') return

    setCreating(true)
    setError(null)
    try {
      const созданный = name.trim()
      await client.createInstance(созданный, provider)
      setName('')
      setProvider('whatsapp')
      setShowForm(false)
      await load()
      toaster.succeeded(`Номер ${вКавычках(созданный)} добавлен`)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setCreating(false)
    }
  }

  return (
    <section aria-label="Номера">
      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {/* Панель раздела: слева — сколько номеров заведено и сколько из них
          на связи, справа — единственное действие раздела. Форма создания
          не занимает верх экрана постоянно: пока номер не добавляют, она
          не нужна, а место под списком нужно всегда.

          Заведён и на связи — разные вещи: счётчик «подключено», считавший
          все записи подряд, обещал связь там, где номер ни разу не
          запускали, и расходился с состоянием на карточках под ним. */}
      <div className="панель-раздела">
        <p className="панель-раздела__итог">
          {instances === null
            ? 'Загружаем номера…'
            : instances.length === 0
              ? 'Ни одного номера не заведено'
              : `Номеров: ${instances.length} · на связи: ${
                  instances.filter((instance) => instance.status === 'connected').length
                }`}
        </p>
        <div className="панель-раздела__действия">
          {/* Ненавязчивый признак фонового обновления — данные под ним не
              меняются на пустоту, только тихо синхронизируются (FR-ADM-017). */}
          {refreshing && (
            <span className="refresh-indicator">
              <IconRefresh /> обновляем…
            </span>
          )}
          {canWrite && (
            <button type="button" className="btn btn--primary" onClick={() => setShowForm(!showForm)}>
              Добавить номер
            </button>
          )}
        </div>
      </div>

      {canWrite && showForm && (
        <div className="card">
          <form className="form-inline" onSubmit={(event) => void handleCreate(event)}>
            <div className="field">
              <label htmlFor="new-instance-name">Как назовём номер</label>
              <input
                id="new-instance-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Отдел продаж"
              />
            </div>
            {/* Канал выбирается при заведении и потом не меняется: от него
                зависит и способ входа, и то, какие узлы доступны сценарию.
                Смена канала у заведённого номера была бы другим номером. */}
            <div className="field">
              <label htmlFor="new-instance-provider">Канал</label>
              <select
                id="new-instance-provider"
                value={provider}
                onChange={(event) => setProvider(event.target.value as ChannelProvider)}
              >
                <option value="whatsapp">{ИМЯ_КАНАЛА.whatsapp}</option>
                <option value="telegram">{ИМЯ_КАНАЛА.telegram}</option>
              </select>
            </div>
            <button type="submit" className="btn btn--primary" disabled={creating}>
              Создать
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </form>
        </div>
      )}

      <div className="card card--plain">
        {instances === null ? (
          <p>Загружаем номера…</p>
        ) : instances.length === 0 ? (
          <div className="empty-state">
            <IconInstances />
            <p>
              Ни одного номера пока не подключено. Добавьте номер — портал покажет
              QR-код, и после сканирования телефоном сообщения начнут приходить сюда.
            </p>
          </div>
        ) : (
          <ul className="entity-list">
            {instances.map((instance) => {
              const rowBusy = busy.has(instance.id)
              const tone = STATUS_TONE[instance.status] ?? 'neutral'
              const meta = statusMeta(instance.status, instance.provider)
              return (
                <li
                  key={instance.id}
                  // Якорь для перехода из командной палитры: своего экрана
                  // у номера нет, карточка живёт здесь (FR-ADM-023).
                  id={якорьНомера(instance.id)}
                  className={`entity-row entity-row--${tone}`}
                >
                  <div className="entity-row__head">
                    <span className="entity-row__title">{instance.name}</span>
                    <span className={`badge badge--${tone}`}>{statusLabel(instance.status)}</span>
                    {/* Канал — подписью, а не цветом: цвет в портале
                        означает состояние, и второе цветное пятно рядом
                        со статусом спорило бы с ним. */}
                    <span className="entity-row__meta">{ИМЯ_КАНАЛА[instance.provider]}</span>
                    {instance.phoneNumber !== null && (
                      <span className="entity-row__meta">{instance.phoneNumber}</span>
                    )}
                  </div>

                  {/* Объяснение состояния и следующий шаг — не разработчику,
                      а человеку (FR-ADM-015, раздел 6 спецификации, правила 1-2). */}
                  <p className="status-explain">{meta.explanation}</p>
                  <p className="status-next">
                    {/* Подписи вопросами, а не «Система:»/«Вы можете:»:
                        объяснения — законченные предложения, и после
                        прежних подписей выходило «Система: Система следит
                        за соединением» и «Вы можете: Действие не требуется»
                        (замечено при проверке в браузере). */}
                    <span>Что происходит: {meta.system}</span>
                    <span>Что делать: {meta.human}</span>
                  </p>

                  {instance.lastError !== null && <Alert>{instance.lastError}</Alert>}

                  <p className="entity-row__links">
                    {/* Без этой ссылки экраны переписки и контактов недостижимы иначе,
                        чем ручным вводом id инстанса в адресную строку (FR-ADM-008). */}
                    <a href={`/app/chats/${instance.id}`}>Переписка</a>
                    <a href={`/app/contacts/${instance.id}`}>Контакты</a>
                    {/* Подписки инстанса: общие видны и здесь — они тоже
                        присылают его уведомления (FR-WA-025). */}
                    <a href={`/app/webhooks/${instance.id}`}>Подписки</a>
                  </p>

                  {PENDING_STATUSES.has(instance.status) && (
                    <AuthPanel
                      client={client}
                      instanceId={instance.id}
                      status={instance.status}
                      provider={instance.provider}
                      onConnected={() => void load()}
                    />
                  )}

                  {canWrite && (
                    <div className="btn-row">
                      {RESTARTABLE_STATUSES.has(instance.status) && (
                        <button
                          type="button"
                          className="btn"
                          disabled={rowBusy}
                          onClick={() => void runAction(instance.id, () => client.startInstance(instance.id))}
                        >
                          Запустить
                        </button>
                      )}
                      {RESTARTABLE_STATUSES.has(instance.status) && (
                        <button
                          type="button"
                          className="btn"
                          disabled={rowBusy}
                          onClick={() => сброс.ask(instance)}
                        >
                          Сбросить авторизацию
                        </button>
                      )}
                      {STOPPABLE_STATUSES.has(instance.status) && (
                        <button
                          type="button"
                          className="btn"
                          disabled={rowBusy}
                          onClick={() => void runAction(instance.id, () => client.stopInstance(instance.id))}
                        >
                          Остановить
                        </button>
                      )}
                      {/* Удаление отодвинуто от остальных действий: оно
                          необратимо, и палец, летящий к «Остановить», не
                          должен попадать в него по соседству. */}
                      <button
                        type="button"
                        className="btn btn--danger btn-row__поодаль"
                        disabled={rowBusy}
                        onClick={() => удаление.ask(instance)}
                      >
                        Удалить
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {удаление.pending !== null && (
        <ConfirmDialog
          title={`Удалить номер ${вКавычках(удаление.pending.name)}?`}
          confirmLabel="Удалить"
          busy={busy.has(удаление.pending.id)}
          onCancel={удаление.cancel}
          onConfirm={() => {
            const цель = удаление.pending
            if (цель === null) return
            удаление.cancel()
            void runAction(цель.id, () => client.removeInstance(цель.id), `Номер ${вКавычках(цель.name)} удалён`)
          }}
        >
          <p>
            Номер отключится от WhatsApp, а вместе с ним исчезнут его переписка, контакты и
            привязка телефона. Подписки, оформленные на этот номер, перестанут получать события.
          </p>
          <p>Это нельзя отменить: чтобы вернуть номер, его придётся завести и привязать заново.</p>
        </ConfirmDialog>
      )}

      {сброс.pending !== null && (
        <ConfirmDialog
          title={`Сбросить авторизацию номера ${вКавычках(сброс.pending.name)}?`}
          confirmLabel="Сбросить авторизацию"
          busy={busy.has(сброс.pending.id)}
          onCancel={сброс.cancel}
          onConfirm={() => {
            const цель = сброс.pending
            if (цель === null) return
            сброс.cancel()
            void runAction(
              цель.id,
              () => client.resetInstance(цель.id),
              `Авторизация номера ${вКавычках(цель.name)} сброшена`,
            )
          }}
        >
          <p>
            Привязка телефона к этому номеру будет стёрта. Чтобы он снова вышел на связь, при
            следующем запуске придётся отсканировать новый код телефоном.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
